import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { siteConfig } from '../../config/site';

const Wrap = styled.section`
  background: linear-gradient(135deg, var(--background-dark) 0%, var(--background-light) 100%);
  padding: 5rem 1rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute; inset: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba(0,153,0,0.15), transparent 55%),
      radial-gradient(circle at 80% 80%, rgba(0,153,0,0.10), transparent 55%);
    pointer-events: none;
  }
`;

const Grid = styled.div`
  position: relative; z-index: 1;
  max-width: 1200px; margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const Left = styled.div`
  color: var(--text-color);
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  border: 1px solid rgba(0,153,0,0.5);
  background: rgba(0,153,0,0.1);
  color: var(--primary-color);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
  .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--primary-color); box-shadow: 0 0 12px var(--primary-color); }
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  line-height: 1.05;
  margin: 0 0 1.25rem;
  span { color: var(--primary-color); }
`;

const Lede = styled.p`
  font-size: 1.15rem;
  line-height: 1.7;
  color: var(--text-secondary);
  margin: 0 0 2rem;
  max-width: 560px;
  @media (max-width: 900px) { margin-left: auto; margin-right: auto; }
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  @media (max-width: 900px) { justify-content: center; }
`;

const PrimaryLink = styled.a`
  background: var(--primary-color);
  color: var(--text-color);
  padding: 1rem 1.9rem;
  border-radius: var(--border-radius);
  font-weight: 700;
  text-decoration: none;
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-2px); }
`;

const GhostLink = styled(Link)`
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 1rem 1.9rem;
  border-radius: var(--border-radius);
  font-weight: 700;
  text-decoration: none;
  transition: background 0.2s ease;
  &:hover { background: rgba(0, 153, 0, 0.1); }
`;

const TrustRow = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 2.5rem;
  flex-wrap: wrap;
  @media (max-width: 900px) { justify-content: center; }
`;

const Stat = styled.div`
  strong {
    display: block;
    font-size: 1.75rem;
    font-weight: 800;
    color: var(--primary-color);
    line-height: 1.1;
  }
  span {
    font-size: 0.85rem;
    color: var(--text-secondary);
  }
`;

const Right = styled.div`
  position: relative;
`;

const MainImage = styled.figure`
  margin: 0;
  border-radius: 1.75rem;
  overflow: hidden;
  border: 1px solid rgba(0,153,0,0.35);
  aspect-ratio: 4/3;
  box-shadow: 0 30px 80px -30px rgba(0,0,0,0.6);
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

const FloatCard = styled.div`
  position: absolute;
  bottom: -1.5rem;
  right: -1rem;
  background: rgba(4, 12, 7, 0.9);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0,153,0,0.35);
  border-radius: 1rem;
  padding: 1rem 1.25rem;
  color: var(--text-color);
  min-width: 200px;
  display: flex;
  gap: 0.75rem;
  align-items: center;
  box-shadow: 0 20px 60px -20px rgba(0,0,0,0.7);

  @media (max-width: 900px) { display: none; }

  .icon {
    width: 40px; height: 40px;
    border-radius: 10px;
    background: rgba(0,153,0,0.15);
    color: var(--primary-color);
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 1.25rem;
  }
  strong { display: block; font-size: 0.95rem; }
  small { color: var(--text-secondary); font-size: 0.8rem; }
`;

const SplitHero: React.FC = () => (
  <Wrap>
    <Grid>
      <Left>
        <Eyebrow><span className="dot" /> Licensed USDT flashing software</Eyebrow>
        <Title>
          Flash USDT across <span>TRC-20, ERC-20 &amp; BEP-20</span> — the reliable way.
        </Title>
        <Lede>
          {siteConfig.brand.name} is a professional flash USDT software for developers,
          traders, and QA teams. Simulate large USDT balances on 50+ real wallets and
          explorers — safely, with 24/7 support and 365-day flash validity.
        </Lede>
        <Actions>
          <PrimaryLink href={`https://wa.me/${siteConfig.contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
            Buy on WhatsApp
          </PrimaryLink>
          <GhostLink to="/pricing">View pricing</GhostLink>
        </Actions>
        <TrustRow>
          <Stat><strong>$10M</strong><span>Daily flash cap</span></Stat>
          <Stat><strong>365d</strong><span>Live flash duration</span></Stat>
          <Stat><strong>50+</strong><span>Wallets supported</span></Stat>
        </TrustRow>
      </Left>
      <Right>
        <MainImage>
          <img
            src="/img/hero-usdt-balance.webp"
            alt={`${siteConfig.brand.name} — flash USDT software preview showing a USDT balance on TRC-20`}
          />
        </MainImage>
        <FloatCard>
          <span className="icon">✓</span>
          <div>
            <strong>Instant delivery</strong>
            <small>License key by email in minutes</small>
          </div>
        </FloatCard>
      </Right>
    </Grid>
  </Wrap>
);

export default SplitHero;
